import React, { useMemo } from 'react';

import { useAppSelector, useTranslate } from '@src/core/hooks';
import CalendarDateSelector from '@src/domains/scheduling/components/DateSelector';
import { useDateSelector } from '@src/domains/products/features/product-booking/hooks/useDateSelector';
import { useGuardedSequentialOpenChange } from '../../hooks/useGuardedSequentialOpenChange';
import { useWidgetId } from '@src/core/IdProvider';
import { currentProductAdderStatus, productAdderStepState } from '@src/stores/product';
import { SequentialSlotShell } from '../SequentialSlotShell';

type Props = {
  loading?: boolean;
};

const DateSelector = ({ loading = false }: Props) => {
  const { i18n } = useTranslate();
  const {
    placeholder,
    selectedDateFormatted,
    disabled,
    open,
    onOpenChange,
    pickerValue,
    referenceDate,
    onMonthChange,
    onConfirm,
    dayRenderer,
    legend,
    totalLabel,
  } = useDateSelector();

  const widgetId = useWidgetId();
  const guardedOnOpenChange = useGuardedSequentialOpenChange('calendar', onOpenChange);
  const { steps } = useAppSelector(productAdderStepState(widgetId));
  const sequentialSlot = steps?.includes('calendar') ? 'calendar' : null;
  const currentStatus = useAppSelector(currentProductAdderStatus(widgetId));
  const isAddingToCart = useMemo(() => currentStatus === 'adding_to_cart', [currentStatus]);

  return (
    <SequentialSlotShell slot={sequentialSlot}>
      <CalendarDateSelector
        placeholder={placeholder}
        selectedDateFormatted={selectedDateFormatted}
        disabled={loading || disabled || isAddingToCart}
        open={open}
        onOpenChange={guardedOnOpenChange}
        pickerValue={pickerValue}
        referenceDate={referenceDate}
        onMonthChange={onMonthChange}
        onConfirm={onConfirm}
        dayRenderer={dayRenderer}
        legend={legend}
        totalLabel={totalLabel}
        labels={{
          confirm: i18n('base_confirm'),
          total: i18n('cart_totalLabel'),
          bottomSheetTitle: i18n('adder_step_calendar'),
        }}
      />
    </SequentialSlotShell>
  );
};

export default DateSelector;
